import { useState, useEffect, useMemo } from 'react';

export function useCoordinatorPhotos(apiBaseUrl, coordinators) {
  const { seeded, missing } = useMemo(() => {
    const list = Array.isArray(coordinators) ? coordinators : [];
    const seededMap = {};
    const missingList = [];
    for (const coord of list) {
      const id = coord?.id;
      if (id == null) continue;
      const embedded = String(coord.profilePhotoUrl || '').trim();
      if (embedded) {
        seededMap[id] = embedded;
        continue;
      }
      if (coord.hasProfilePhoto === false) continue;
      missingList.push(coord);
    }
    return { seeded: seededMap, missing: missingList };
  }, [coordinators]);

  const [fetchedMap, setFetchedMap] = useState({});
  const idsKey = missing.map((c) => c.id).join(',');

  useEffect(() => {
    if (!apiBaseUrl || !idsKey) {
      setFetchedMap({});
      return undefined;
    }

    let cancelled = false;
    const token = localStorage.getItem('authToken');
    const ids = idsKey.split(',').map((x) => Number(x)).filter((n) => Number.isFinite(n));

    const load = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/users/profile-photos/urls`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ ids }),
        });
        const data = await res.json();
        if (!cancelled && data?.success && data?.urls && typeof data.urls === 'object') {
          const mapped = {};
          for (const id of ids) {
            mapped[id] = data.urls[String(id)] || null;
          }
          setFetchedMap(mapped);
          return;
        }
      } catch {
        /* fallback */
      }

      const entries = await Promise.all(
        missing.map(async (coord) => {
          try {
            const res = await fetch(`${apiBaseUrl}/api/users/${coord.id}/profile-photo/url`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            return [coord.id, data.success && data.viewUrl ? data.viewUrl : null];
          } catch {
            return [coord.id, null];
          }
        })
      );
      if (!cancelled) setFetchedMap(Object.fromEntries(entries));
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, idsKey]);

  return useMemo(() => ({ ...seeded, ...fetchedMap }), [seeded, fetchedMap]);
}
